import React, { useState } from 'react';
import Button from 'components/ui/Button';
import Input from 'components/ui/Input';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';

const ProductTable = ({ 
  products, 
  selectedProducts, 
  onProductSelect, 
  onSelectAll, 
  onProductClick,
  onGenerateQR,
  onDownloadQr,
  onViewProduct,
  haspermission,
}) => {
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // console.log(products)

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedProducts = [...products].sort((a, b) => {
    const aValue = a[sortField];
    const bValue = b[sortField];
    
    if (typeof aValue === 'string') {
      return sortDirection === 'asc' 
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }
    
    return sortDirection === 'asc' 
      ? aValue - bValue
      : bValue - aValue;
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentProducts = sortedProducts.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(sortedProducts.length / itemsPerPage);

  const handleSelectAll = (e) => {
    onSelectAll(e.target.checked);
  };

  const getStockStatus = (stock) => {
    if (stock === 0) return { label: 'Out of Stock', color: 'text-red-600 bg-red-50' };
    if (stock < 20) return { label: 'Low Stock', color: 'text-yellow-600 bg-yellow-50' };
    return { label: 'In Stock', color: 'text-green-600 bg-green-50' };
  };

  const getQRStatus = (status) => {
    if (status === 'generated') return { label: 'Generated', color: 'text-green-600 bg-green-50' };
    if (status === 'pending') return { label: 'Pending', color: 'text-yellow-600 bg-yellow-50' };
    return { label: 'Not Generated', color: 'text-gray-600 bg-gray-50' };
  };

  const SortButton = ({ field, children }) => (
    <button
      onClick={() => handleSort(field)}
      className="flex items-center space-x-1 hover:bg-gray-50 p-1 rounded"
    >
      <span>{children}</span>
      <Icon 
        name={sortField === field && sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown'} 
        size={14} 
        color="#7f8c8d" 
      />
    </button>
  );

  return (
    <div className="bg-surface rounded-lg border border-border overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-border">
            <tr>
              <th className="px-6 py-3 text-left">
                <Input
                  type="checkbox"
                  checked={(selectedProducts?.length || 0 ) === (products?.length ||0 ) && products?.length > 0}
                  onChange={handleSelectAll}
                  className="rounded"
                />
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <SortButton field="name">Product Name</SortButton>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <SortButton field="product_code">Product Code</SortButton>
              </th>
              
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <SortButton field="stock">Client Name</SortButton>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 tracking-wider">
                Total Number Batches
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200 ">
            {currentProducts.map((product) => {
              const stockStatus = getStockStatus(product.stock);
              const qrStatus = getQRStatus(product.qrCode);

              return (
                <tr key={product.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Input
                      type="checkbox"
                      checked={selectedProducts.includes(product.id)}
                      onChange={() => onProductSelect(product.id)}
                      className="rounded"
                    />
                  </td>
                  <td className="whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                         <Image
                            // src={'https://hiraadmin.opaldemo.com/'+product.image}
                            src={`${import.meta.env.VITE_APP_ADMINURL}/${product.image}`}
                            alt={product.title}
                            className="w-10 h-10 rounded-full object-contain"
                          />
                      </div>
                      <div className="ml-4">
                        <div 
                          className="text-sm font-medium text-gray-900 cursor-pointer hover:text-primary"
                          onClick={() => onProductClick(product)}
                        >
                          {product.title}
                        </div>
                        {/* <div className="text-sm text-gray-500">{product.description}</div> */}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {product.product_code}
                  </td>
                  
                  <td className="whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-warning-500 text-white`}>
                      {product.users?.company_name || '-'}
                      
                    </span>
                  </td>
                  <td className="whitespace-nowrap text-center text-sm">
                    {product.batches?.length ?? 0}
                  </td>
                  <td className="whitespace-nowrap pr-4 ">
                    <div className="flex justify-center space-x-2">
                      <Button
                      variant="ghost"
                      size="sm"
                      iconName="Eye"
                      title="View Batch Items"
                      onClick={()=> onViewProduct(product)}
                      className="hover:text-white "
                      />
                      {/* <Button
                        variant="ghost"
                        size="sm"
                        iconName="Edit"
                        onClick={() => onProductClick(product)}
                        className="text-gray-400 hover:text-gray-600"
                      /> */}
                      {haspermission("Create QR Code") &&
                        <Button
                        variant="ghost"
                        size="sm"
                        iconName="QrCode"
                        title="Create Qr Code"
                        onClick={() => onGenerateQR(product.id)}
                        className="hover:text-white"
                        />
                      }
                      { haspermission('Export Data') &&
                        <Button
                        variant="primary"
                        size="sm"
                        iconName="Download"
                        title="Download Qr Code Excel"
                        onClick={() => onDownloadQr(product.id)}
                        className="text-white hover:text-blue-600 hover:bg-white"
                        />
                      }
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="bg-white px-6 py-3 border-t border-border flex items-center justify-between">
          <div className="flex items-center text-sm text-gray-500">
            Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, sortedProducts.length)} of {sortedProducts.length} products
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              iconName="ChevronLeft"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(currentPage - 1)}
            />
            <div className="flex space-x-1">
              {[...Array(totalPages)].map((_, index) => (
                <Button
                  key={index + 1}
                  variant={currentPage === index + 1 ? "primary" : "outline"}
                  size="sm"
                  onClick={() => setCurrentPage(index + 1)}
                >
                  {index + 1}
                </Button>
              ))}
            </div>
            <Button
              variant="outline"
              size="sm"
              iconName="ChevronRight"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(currentPage + 1)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductTable;